import type * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { StoryCard } from '@org/model';
import type { StoryLabels } from './labels';
import { StoryNotebookCard } from './notebook-card';
import { StoryQuoteCard } from './quote-card';
import type { StoryCardComponentProps } from './story-types';
import { STORY_CARD_LABEL_KEYS } from './story-utils';
import { StoryTextCard } from './text-card';
import { StoryVideoCard } from './video-card';

export type StoryCardOfType<T extends StoryCard['type']> = Extract<StoryCard, { type: T }>;

export interface StoryCardRegistryEntry<T extends StoryCard['type'] = StoryCard['type']> {
  /** renders the card inside a story page */
  Component: React.ComponentType<StoryCardComponentProps<StoryCardOfType<T>>>;
  /** a new, empty card of this type (valid against `storyCardSchema`, fresh id) */
  defaults: () => StoryCardOfType<T>;
  /** the `StoryLabels` key that names this card type ("Text", "Video"…) */
  labelKey: keyof StoryLabels;
}

const empty = () => ({ en: '', pt: '' });

/**
 * One entry per card template. To add a card type: add its schema to
 * `@org/model` (woman-story.ts), write its component here, and register it —
 * the viewer, the thumbnails and the admin editor pick it up from this map.
 */
export const STORY_CARD_REGISTRY: { [T in StoryCard['type']]: StoryCardRegistryEntry<T> } = {
  text: {
    Component: StoryTextCard,
    defaults: () => ({ type: 'text', id: uuidv4(), title: empty(), big: '', theme: 'ink', body: empty(), vocab: [] }),
    labelKey: STORY_CARD_LABEL_KEYS.text,
  },
  video: {
    Component: StoryVideoCard,
    defaults: () => ({ type: 'video', id: uuidv4(), title: empty(), source: 'upload', url: '', posterUrl: '', caption: empty() }),
    labelKey: STORY_CARD_LABEL_KEYS.video,
  },
  notebook: {
    Component: StoryNotebookCard,
    defaults: () => ({
      type: 'notebook',
      id: uuidv4(),
      title: empty(),
      date: empty(),
      original: '',
      originalLang: 'en',
      source: empty(),
      gloss: empty(),
      vocab: [],
    }),
    labelKey: STORY_CARD_LABEL_KEYS.notebook,
  },
  quote: {
    Component: StoryQuoteCard,
    defaults: () => ({ type: 'quote', id: uuidv4(), title: empty(), body: empty() }),
    labelKey: STORY_CARD_LABEL_KEYS.quote,
  },
};

/** A new empty card of the given type, with a generated id. */
export function createStoryCard<T extends StoryCard['type']>(type: T): StoryCardOfType<T> {
  return STORY_CARD_REGISTRY[type].defaults() as StoryCardOfType<T>;
}
